import { apiClient } from "./browser";
import type { components } from "./generated/wio-veyra-api";

type Schemas = components["schemas"];
export type WorkspaceContext = Schemas["TenantAdministrationContextResponse"];
export type Supplier = Schemas["SupplierResponse"];
export type SupplierContact = Schemas["SupplierContactResponse"];
export type SupplierAccess = Schemas["SupplierPortalAccessResponse"];
export type SupplierInvitation = Schemas["SupplierPortalInvitationResponse"];
export type SupplierAccessList = Schemas["SupplierPortalAccessListResponse"];
export type SupplierSession = Schemas["SupplierSessionResponse"];
export type SupplierPortalContext = Schemas["SupplierPortalContextResponse"];
export type Operation = Schemas["OperationResponse"];

interface SignalOptions {
  signal?: AbortSignal;
}

interface TenantOptions extends SignalOptions {
  tenantId: string;
}

interface PageOptions extends TenantOptions {
  offset: number;
  limit: number;
}

interface SupplierOptions extends TenantOptions {
  supplierId: string;
}

export interface CommandOptions extends TenantOptions {
  csrfToken: string;
  idempotencyKey: string;
}

const tenantPath = (tenantId: string, suffix: string) =>
  `/tenants/${encodeURIComponent(tenantId)}${suffix}` as const;
const supplierPath = (options: SupplierOptions, suffix = "") =>
  tenantPath(
    options.tenantId,
    `/suppliers/${encodeURIComponent(options.supplierId)}${suffix}`,
  );

function query<T>(operation: string, path: `/${string}`, signal?: AbortSignal) {
  return apiClient.request<T>({
    operation,
    kind: "query",
    method: "GET",
    path,
    signal,
  });
}

function command<T, B>(
  operation: string,
  method: "POST" | "PUT",
  path: `/${string}`,
  options: CommandOptions,
  body: B,
  expectedVersion?: string,
) {
  return apiClient.request<T, B>({
    operation,
    kind: "command",
    method,
    path,
    body,
    antiforgeryToken: options.csrfToken,
    idempotencyKey: options.idempotencyKey,
    expectedVersion,
    signal: options.signal,
  });
}

export const getWorkspaceContext = (options: TenantOptions) =>
  query<WorkspaceContext>(
    "GetTenantWorkspaceContext",
    tenantPath(options.tenantId, "/workspace/context"),
    options.signal,
  );
const pageQuery = (offset: number, limit: number) =>
  new URLSearchParams({ offset: String(offset), limit: String(limit) });

export const listSuppliers = (options: PageOptions) =>
  query<Schemas["SupplierPageResponse"]>(
    "ListTenantSuppliers",
    tenantPath(
      options.tenantId,
      `/suppliers?${pageQuery(options.offset, options.limit)}`,
    ),
    options.signal,
  );
export const getSupplier = (options: SupplierOptions) =>
  query<Supplier>("GetTenantSupplier", supplierPath(options), options.signal);
export const listContacts = (options: SupplierOptions & PageOptions) =>
  query<Schemas["SupplierContactPageResponse"]>(
    "ListSupplierContacts",
    supplierPath(
      options,
      `/contacts?${pageQuery(options.offset, options.limit)}`,
    ),
    options.signal,
  );
export const listPortalAccess = (options: SupplierOptions) =>
  query<SupplierAccessList>(
    "ListSupplierPortalAccess",
    supplierPath(options, "/portal-access"),
    options.signal,
  );

export const createSupplier = (
  options: CommandOptions,
  body: Schemas["CreateSupplierRequest"],
) =>
  command<Supplier, typeof body>(
    "CreateTenantSupplier",
    "POST",
    tenantPath(options.tenantId, "/suppliers"),
    options,
    body,
  );

export const updateSupplier = (
  options: CommandOptions,
  supplierId: string,
  body: Schemas["UpdateSupplierRequest"],
) =>
  command<Supplier, typeof body>(
    "UpdateTenantSupplier",
    "PUT",
    supplierPath({ ...options, supplierId }),
    options,
    body,
    body.expectedVersion == null ? undefined : String(body.expectedVersion),
  );

export const createContact = (
  options: CommandOptions,
  supplierId: string,
  body: Schemas["CreateSupplierContactRequest"],
) =>
  command<Schemas["SupplierContactMutationResponse"], typeof body>(
    "CreateSupplierContact",
    "POST",
    supplierPath({ ...options, supplierId }, "/contacts"),
    options,
    body,
  );

export const updateContact = (
  options: CommandOptions,
  supplierId: string,
  contactId: string,
  body: Schemas["UpdateSupplierContactRequest"],
) =>
  command<Schemas["SupplierContactMutationResponse"], typeof body>(
    "UpdateSupplierContact",
    "PUT",
    supplierPath(
      { ...options, supplierId },
      `/contacts/${encodeURIComponent(contactId)}`,
    ),
    options,
    body,
    body.expectedVersion == null ? undefined : String(body.expectedVersion),
  );

export const deactivateContact = (
  options: CommandOptions,
  supplierId: string,
  contactId: string,
  expectedVersion: number | string,
) =>
  command<
    Schemas["SupplierContactMutationResponse"],
    Schemas["DeactivateSupplierContactRequest"]
  >(
    "DeactivateSupplierContact",
    "POST",
    supplierPath(
      { ...options, supplierId },
      `/contacts/${encodeURIComponent(contactId)}/deactivate`,
    ),
    options,
    { expectedVersion },
    String(expectedVersion),
  );

export const createPortalInvitation = (
  options: CommandOptions,
  supplierId: string,
  contactId: string,
) =>
  command<
    Schemas["CreateSupplierPortalInvitationResponse"],
    Schemas["CreateSupplierPortalInvitationRequest"]
  >(
    "CreateSupplierPortalInvitation",
    "POST",
    supplierPath({ ...options, supplierId }, "/portal-access/invitations"),
    options,
    { contactId },
  );

export const revokePortalAccess = (
  options: CommandOptions,
  supplierId: string,
  accessId: string,
  expectedVersion: number | string,
) =>
  command<
    Schemas["RevokeSupplierPortalAccessResponse"],
    Schemas["RevokeSupplierPortalAccessRequest"]
  >(
    "RevokeSupplierPortalAccess",
    "POST",
    supplierPath(
      { ...options, supplierId },
      `/portal-access/${encodeURIComponent(accessId)}/revoke`,
    ),
    options,
    { expectedVersion },
    String(expectedVersion),
  );

export function getOperationByKey(
  options: TenantOptions,
  operationType: string,
  idempotencyKey: string,
) {
  const params = new URLSearchParams({ operationType, idempotencyKey });
  return query<Operation>(
    "GetTenantOperationResultByKey",
    tenantPath(options.tenantId, `/operations/by-key?${params}`),
    options.signal,
  );
}

export const getSupplierSession = (options: SignalOptions = {}) =>
  query<SupplierSession>(
    "GetSupplierSession",
    "/supplier-session",
    options.signal,
  );
export const getSupplierPortalContext = (options: SupplierOptions) =>
  query<SupplierPortalContext>(
    "GetSupplierPortalContext",
    tenantPath(
      options.tenantId,
      `/supplier-portal/suppliers/${encodeURIComponent(options.supplierId)}/context`,
    ),
    options.signal,
  );
export const logoutSupplierSession = (
  csrfToken: string,
  signal?: AbortSignal,
) =>
  apiClient.request<void>({
    operation: "LogoutSupplierSession",
    kind: "command",
    method: "POST",
    path: "/supplier-session/logout",
    antiforgeryToken: csrfToken,
    signal,
  });

export function activateSupplier(
  body: Schemas["SupplierActivationRequest"],
  csrfToken: string,
  signal?: AbortSignal,
) {
  return apiClient.request<Schemas["SupplierActivationResponse"], typeof body>({
    operation: "ActivateSupplierPortalAccess",
    kind: "command",
    method: "POST",
    path: "/identity/supplier-activations",
    body,
    antiforgeryToken: csrfToken,
    signal,
  });
}
